import { Persona, PersonaFiltro } from '../../model/persona/persona';
import { PageResponse } from '../../model/common/page-response.dto';

/**
 * Puerto Abstracto: PersonaRepository
 * 
 * Define el contrato que deben implementar los adaptadores de infraestructura.
 * Trabaja con la entidad de dominio Persona.
 * 
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpPersonaRepository (adaptador) lo implementa en infraestructura
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export abstract class PersonaRepository {
  
  /**
   * Obtiene todas las personas
   */
  abstract getAll(): Promise<Persona[]>;
  
  /**
   * Obtiene una persona por ID
   */
  abstract getById(cdpersona: number): Promise<Persona>;
  
  /**
   * Busca persona por identificación
   */
  abstract getByIdentificacion(tipoId: string, numeroId: string): Promise<Persona | null>;
  
  /**
   * Verifica si existe una persona
   */
  abstract exists(tipoId: string, numeroId: string): Promise<boolean>;
  
  /**
   * Crea una nueva persona
   */
  abstract create(persona: Persona): Promise<Persona>;
  
  /**
   * Actualiza una persona existente
   */
  abstract update(cdpersona: number, persona: Persona): Promise<Persona>;
  
  /**
   * Elimina (inactiva) una persona
   */
  abstract delete(cdpersona: number): Promise<void>;
  
  /**
   * Busca personas por nombre
   */
  abstract searchByNombre(nombre: string): Promise<Persona[]>;
  
  /**
   * Obtiene personas activas
   */
  abstract getActivos(): Promise<Persona[]>;
  
  /**
   * Cuenta el total de personas
   */
  abstract count(): Promise<number>;
  
  // =========================================================================
  // MÉTODOS DE PAGINACIÓN CON FILTROS
  // =========================================================================
  
  /**
   * Obtiene personas paginadas con filtros de búsqueda
   */
  abstract getPageWithFilters(cursor?: string, size?: number, filtro?: PersonaFiltro): Promise<PageResponse<Persona>>;
  
  /**
   * Obtiene personas paginadas con filtros y dirección específica
   * @param direction 'NEXT' para siguiente página, 'PREVIOUS' para anterior
   */
  abstract getPageWithFiltersAndDirection(
    cursor?: string, 
    size?: number, 
    filtro?: PersonaFiltro,
    direction?: 'NEXT' | 'PREVIOUS'
  ): Promise<PageResponse<Persona>>;
  
  /**
   * Cuenta personas con filtros de búsqueda
   */
  abstract countWithFilters(filtro?: PersonaFiltro): Promise<number>;
  
  /**
   * Obtiene la última página de personas
   */
  abstract getLastPage(size?: number, filtro?: PersonaFiltro): Promise<PageResponse<Persona>>;
}
